import React, { useState, useEffect } from 'react';
import { runAllEmailTests, testEdgeFunction, testFriendInvitationEmail, testFeedbackNotificationEmail, testFeedbackConfirmationEmail, testPasswordResetEmail } from '@/utils/emailTest';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Icon from '@/components/ui/Icon';
import { supabase } from '@/lib/supabase';

/**
 * Email Testing Page
 * 
 * This page provides a UI for testing all email functionality.
 * Access at /email-test (add route in your router)
 */
export default function EmailTestPage() {
  const [testEmail, setTestEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    
    checkAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  const handleTest = async (testFunction, testName) => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      // Check authentication first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You must be logged in to test email functionality. Please refresh the page and log in first.');
        setIsLoading(false);
        return;
      }

      const result = await testFunction(testEmail);
      setResults(prev => ({
        ...prev,
        [testName]: result
      }));
    } catch (err) {
      setError(err.message);
      console.error('Test error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunAllTests = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    if (!isAuthenticated) {
      setError('You must be logged in to test email functionality. Please refresh the page and log in first.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const allResults = await runAllEmailTests(testEmail);
      setResults(allResults);
    } catch (err) {
      setError(err.message);
      console.error('Test error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-transparent rounded-lg p-0 shadow-none">

        {!isAuthenticated && (
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-700">
              <strong>⚠️ Authentication Required:</strong> You must be logged in to test email functionality. 
              Please refresh the page and log in first.
            </AlertDescription>
          </Alert>
        )}

        {/* Test Email Input */}
        <div className="mb-6">
          <Label htmlFor="test-email" className="mb-2 block">
            Test Email Address
          </Label>
          <div className="flex gap-2">
            <Input
              id="test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="your-email@example.com"
              className="flex-1"
            />
            <Button
              onClick={handleRunAllTests}
              disabled={isLoading || !testEmail}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {isLoading ? (
                <>
                  <Icon name="loader" size="sm" className="mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Icon name="play" size="sm" className="mr-2" />
                  Run All Tests
                </>
              )}
            </Button>
          </div>
        </div>

        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {/* Individual Test Buttons */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3">Individual Tests</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              onClick={() => handleTest(testEdgeFunction, 'edgeFunction')}
              disabled={isLoading || !testEmail}
              variant="outline"
              className="justify-start"
            >
              <Icon name="mail" size="sm" className="mr-2" />
              Test Edge Function
            </Button>

            <Button
              onClick={() => handleTest(testFriendInvitationEmail, 'friendInvitation')}
              disabled={isLoading || !testEmail}
              variant="outline"
              className="justify-start"
            >
              <Icon name="user-plus" size="sm" className="mr-2" />
              Test Friend Invitation
            </Button>

            <Button
              onClick={() => handleTest(testFeedbackNotificationEmail, 'feedbackNotification')}
              disabled={isLoading || !testEmail}
              variant="outline"
              className="justify-start"
            >
              <Icon name="bell" size="sm" className="mr-2" />
              Test Feedback Notification
            </Button>

            <Button
              onClick={() => handleTest(testFeedbackConfirmationEmail, 'feedbackConfirmation')}
              disabled={isLoading || !testEmail}
              variant="outline"
              className="justify-start"
            >
              <Icon name="check-circle" size="sm" className="mr-2" />
              Test Feedback Confirmation
            </Button>

            <Button
              onClick={() => handleTest(testPasswordResetEmail, 'passwordReset')}
              disabled={isLoading || !testEmail}
              variant="outline"
              className="justify-start"
            >
              <Icon name="key" size="sm" className="mr-2" />
              Test Password Reset
            </Button>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-3">Test Results</h2>
            <div className="space-y-2">
              {Object.entries(results).map(([testName, result]) => (
                <div
                  key={testName}
                  className={`p-3 rounded-lg border ${
                    result?.success
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium capitalize">
                      {testName.replace(/([A-Z])/g, ' $1').trim()}:
                    </span>
                    <span className={result?.success ? 'text-green-700' : 'text-red-700'}>
                      {result?.success ? (
                        <>
                          <Icon name="check-circle" size="sm" className="inline mr-1" />
                          PASSED
                        </>
                      ) : (
                        <>
                          <Icon name="x-circle" size="sm" className="inline mr-1" />
                          FAILED
                        </>
                      )}
                    </span>
                  </div>
                  {result?.messageId && (
                    <div className="text-sm text-slate-600 mt-1">
                      Message ID: {result.messageId}
                    </div>
                  )}
                  {result?.error && (
                    <div className="text-sm text-red-600 mt-1">
                      Error: {result.error}
                    </div>
                  )}
                  {result?.diagnostic && (
                    <div className="text-sm text-amber-600 mt-1">
                      ⚠️ {result.diagnostic}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Next Steps:</strong>
              </p>
              <ul className="text-sm text-slate-600 mt-2 list-disc list-inside space-y-1">
                <li>Check your email inbox for test emails</li>
                <li>Verify emails appear in Resend dashboard logs</li>
                <li>Check Supabase Edge Function logs for any errors</li>
                <li>Review the <a href="/email-testing-guide" className="text-brand-600 underline">testing guide</a> for troubleshooting</li>
              </ul>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">Testing Instructions</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Enter a valid email address you can access</li>
            <li>Click "Run All Tests" to test everything, or test individually</li>
            <li>Check your email inbox for test emails</li>
            <li>Verify results in Resend dashboard and Supabase logs</li>
            <li>Review any error messages and fix configuration issues</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

